import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, MapPin, Users, ChevronRight, CheckCircle2, ArrowRight, Sparkles, Loader2, AlertCircle } from "lucide-react";
import { useSettings } from "../../lib/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/jardins-ole`;

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
function fmtShortDate(iso) {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }); }
  catch { return iso; }
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export function WorkshopsListPage() {
  const { settings: s } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/workshops`)
      .then(r => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <title>{`Ateliers gratuits — ${s?.company_name || "Les Jardins d'Éole"}`}</title>
      <meta name="description" content="Ateliers jardin gratuits à Istres : potager, taille, massif méditerranéen, arrosage. Places limitées à 10 personnes par session." />

      <section className="pt-14 pb-10 bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-white/60 mb-4 flex items-center gap-2">
            <Link to="/">Accueil</Link><ChevronRight className="w-3 h-3"/>
            <span>Ateliers</span>
          </nav>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-300 font-bold mb-3">
            <Sparkles className="w-3.5 h-3.5"/> 100% gratuit · 10 places par session
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black leading-tight">
            Nos ateliers jardin
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl text-base md:text-lg">
            Apprenez les bons gestes auprès de notre équipe : potager, taille, conception
            de massif, arrosage économe. Places limitées — réservez tôt.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="text-center py-20 text-sm text-[hsl(var(--muted-foreground))]">
              <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2"/>Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 border-2 border-dashed rounded-3xl">
              <Calendar className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]/30 mb-2"/>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Aucun atelier programmé pour le moment. Revenez bientôt !
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="workshops-grid">
              {items.map(w => <WorkshopCard key={w.id} w={w} />)}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function WorkshopCard({ w }) {
  const sold = w.seats_left === 0;
  const almost = !sold && w.seats_left <= 3;
  return (
    <Link
      to={`/ateliers/${w.slug}`}
      className="group block rounded-3xl overflow-hidden bg-white border border-[hsl(var(--border))] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
      data-testid={`workshop-card-${w.slug}`}
    >
      <div className="relative aspect-[4/3] bg-[hsl(var(--muted))]/30 overflow-hidden">
        {w.image
          ? <img src={w.image} alt={w.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
          : <div className="w-full h-full flex items-center justify-center"><Calendar className="w-12 h-12 text-[color:var(--brand-navy)]/20"/></div>
        }
        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-2xl px-3 py-2 text-center shadow-md min-w-[64px]">
          <div className="text-[10px] uppercase font-bold text-[color:var(--brand-sky)]">{fmtShortDate(w.starts_at).split(" ")[1] || ""}</div>
          <div className="font-display text-2xl font-black text-[color:var(--brand-navy)] leading-none">{fmtShortDate(w.starts_at).split(" ")[0]}</div>
          <div className="text-[10px] mt-1 text-[hsl(var(--muted-foreground))]">{fmtTime(w.starts_at)}</div>
        </div>
        <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-full text-xs font-bold shadow-md ${
          sold ? "bg-red-600 text-white" :
          almost ? "bg-amber-500 text-white" :
          "bg-emerald-500 text-white"
        }`}>
          {sold ? "Complet" : `${w.seats_left} place${w.seats_left>1?"s":""}`}
        </div>
        {w.price_cents === 0 && (
          <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-full bg-amber-300 text-[color:var(--brand-navy)] text-xs font-black uppercase tracking-wider shadow-md">
            Gratuit
          </div>
        )}
      </div>
      <div className="p-5">
        {w.category && (
          <div className="text-[11px] font-bold uppercase tracking-wider text-[color:var(--brand-sky)] mb-2">
            {w.category}
          </div>
        )}
        <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)] mb-2 line-clamp-2">{w.title}</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-3">{w.description}</p>
        <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5"/> {w.duration_min} min</span>
          <span className="inline-flex items-center gap-1 text-[color:var(--brand-navy)] font-bold group-hover:translate-x-1 transition-transform">
            S'inscrire <ArrowRight className="w-3.5 h-3.5"/>
          </span>
        </div>
      </div>
    </Link>
  );
}

export function WorkshopDetailPage() {
  const { slug } = useParams();
  const { settings: s } = useSettings();
  const [w, setW] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", seats: 1, notes: "", accept_newsletter: true });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [submitErr, setSubmitErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/workshops/${slug}`)
      .then(r => setW(r.data))
      .catch(() => setError("not_found"));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr("");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/workshops/${slug}/register`, {
        ...form, seats: Number(form.seats) || 1,
      });
      setDone(r.data);
    } catch (err) {
      setSubmitErr(err?.response?.data?.detail || "Erreur lors de l'inscription. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) return <div className="py-24 text-center text-[hsl(var(--muted-foreground))]">Atelier introuvable.</div>;
  if (!w) return <div className="py-24 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin"/></div>;

  const sold = w.seats_left === 0;

  return (
    <>
      <title>{`${w.title} — ${s?.company_name || "Les Jardins d'Éole"}`}</title>
      <meta name="description" content={w.description}/>

      <article className="py-10 md:py-14">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-[hsl(var(--muted-foreground))] mb-4 flex items-center gap-2 flex-wrap">
            <Link to="/">Accueil</Link><ChevronRight className="w-3 h-3"/>
            <Link to="/ateliers">Ateliers</Link><ChevronRight className="w-3 h-3"/>
            <span>{w.title}</span>
          </nav>

          <div className="grid lg:grid-cols-[3fr_2fr] gap-8">
            <div>
              {w.image && (
                <div className="rounded-3xl overflow-hidden aspect-video bg-[hsl(var(--muted))]/30 shadow-xl">
                  <img src={w.image} alt={w.title} className="w-full h-full object-cover"/>
                </div>
              )}
              {w.category && (
                <div className="mt-6 text-[11px] font-bold uppercase tracking-wider text-[color:var(--brand-sky)]">{w.category}</div>
              )}
              <h1 className="mt-2 font-display text-3xl md:text-5xl font-black text-[color:var(--brand-navy)] leading-tight">{w.title}</h1>
              <p className="mt-4 text-base md:text-lg text-[hsl(var(--foreground))]/80 leading-relaxed">{w.description}</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <Info icon={Calendar} label="Date" value={fmtDate(w.starts_at)}/>
                <Info icon={Clock} label="Durée" value={`${w.duration_min} minutes`}/>
                <Info icon={MapPin} label="Lieu" value={w.location}/>
                <Info icon={Users} label="Places" value={`${w.seats_left} / ${w.max_seats} disponibles`}/>
              </div>

              {w.long_description && (
                <div className="mt-8 prose max-w-none text-base md:text-lg text-[hsl(var(--foreground))] whitespace-pre-line leading-relaxed">
                  {w.long_description}
                </div>
              )}
            </div>

            <aside className="lg:sticky lg:top-24 self-start">
              <div className="rounded-3xl border border-[hsl(var(--border))] bg-white shadow-xl p-6 md:p-7" data-testid="workshop-register-card">
                {done ? (
                  <div className="text-center py-6">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3"/>
                    <h3 className="font-display text-2xl font-black text-[color:var(--brand-navy)]">Inscription confirmée !</h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
                      Vous recevrez un rappel par email quelques jours avant l'atelier.
                    </p>
                    <p className="mt-4 text-xs font-semibold text-[color:var(--brand-sky)]">
                      Il reste {done.seats_left} place(s) sur cet atelier.
                    </p>
                  </div>
                ) : sold ? (
                  <div className="text-center py-6">
                    <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3"/>
                    <h3 className="font-display text-2xl font-black text-[color:var(--brand-navy)]">Atelier complet</h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
                      Toutes les places sont prises. Découvrez nos prochaines sessions.
                    </p>
                    <Link to="/ateliers" className="inline-flex items-center gap-2 mt-5 px-5 h-11 rounded-full bg-[color:var(--brand-navy)] text-white font-bold text-sm">
                      Autres ateliers <ArrowRight className="w-4 h-4"/>
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-3" data-testid="workshop-register-form">
                    <div className="flex items-baseline justify-between mb-2">
                      <h3 className="font-display text-xl font-black text-[color:var(--brand-navy)]">Réserver ma place</h3>
                      {w.price_cents === 0 && (
                        <span className="text-xs font-black uppercase tracking-wider text-amber-600 bg-amber-100 px-2 py-1 rounded-full">Gratuit</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                      <Field label="Prénom *" value={form.first_name} onChange={v => setForm({...form, first_name: v})} required testId="workshop-first-name"/>
                      <Field label="Nom" value={form.last_name} onChange={v => setForm({...form, last_name: v})} testId="workshop-last-name"/>
                    </div>
                    <Field label="Email *" type="email" value={form.email} onChange={v => setForm({...form, email: v})} required testId="workshop-email"/>
                    <Field label="Téléphone" type="tel" value={form.phone} onChange={v => setForm({...form, phone: v})} testId="workshop-phone"/>
                    <div>
                      <label className="text-xs font-semibold text-[color:var(--brand-navy)] mb-1 block">Nombre de places (max {Math.min(4, w.seats_left)})</label>
                      <input
                        type="number" min={1} max={Math.min(4, w.seats_left)}
                        value={form.seats}
                        onChange={e => setForm({...form, seats: e.target.value})}
                        className="w-full h-10 px-3 rounded-lg border border-[hsl(var(--border))] focus:border-[color:var(--brand-sky)] outline-none text-sm"
                        data-testid="workshop-seats"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[color:var(--brand-navy)] mb-1 block">Message (facultatif)</label>
                      <textarea
                        rows={2} value={form.notes}
                        onChange={e => setForm({...form, notes: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[color:var(--brand-sky)] outline-none text-sm resize-none"
                        placeholder="Allergies, débutant, accompagnant…"
                        data-testid="workshop-notes"
                      />
                    </div>
                    <label className="flex items-start gap-2 p-2.5 rounded-lg bg-[hsl(var(--muted))]/30 cursor-pointer hover:bg-[hsl(var(--muted))]/50 transition-colors" data-testid="workshop-newsletter-label">
                      <input
                        type="checkbox"
                        checked={form.accept_newsletter}
                        onChange={e => setForm({...form, accept_newsletter: e.target.checked})}
                        className="mt-0.5 w-4 h-4 accent-[color:var(--brand-sky)]"
                        data-testid="workshop-newsletter-checkbox"
                      />
                      <span className="text-xs text-[hsl(var(--foreground))]/80 leading-snug">
                        Je souhaite recevoir les <strong className="text-[color:var(--brand-navy)]">conseils mensuels</strong> de Sophie & Pascal (calendrier potager, tutos saisonniers). Désinscription en 1 clic.
                      </span>
                    </label>
                    {submitErr && (
                      <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"/>{submitErr}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-12 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg disabled:opacity-60"
                      data-testid="workshop-register-submit"
                    >
                      {submitting
                        ? <><Loader2 className="w-4 h-4 animate-spin"/>Inscription…</>
                        : <>Confirmer mon inscription <ArrowRight className="w-4 h-4"/></>}
                    </button>
                    <p className="text-[11px] text-[hsl(var(--muted-foreground))] text-center mt-2">
                      Atelier {w.price_cents === 0 ? "100% gratuit" : "payant"} · Annulation possible jusqu'à 48h avant
                    </p>
                  </form>
                )}
              </div>
            </aside>
          </div>
        </div>
      </article>
    </>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-[hsl(var(--muted))]/40">
      <Icon className="w-4 h-4 mt-0.5 text-[color:var(--brand-sky)] flex-shrink-0"/>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">{label}</div>
        <div className="text-sm font-semibold text-[color:var(--brand-navy)] capitalize">{value}</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testId = "" }) {
  return (
    <div>
      <label className="text-xs font-semibold text-[color:var(--brand-navy)] mb-1 block">{label}</label>
      <input
        type={type}
        value={value}
        required={required}
        onChange={e => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-lg border border-[hsl(var(--border))] focus:border-[color:var(--brand-sky)] outline-none text-sm"
        data-testid={testId}
      />
    </div>
  );
}
